/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * TRYB ŹRÓDŁA — obserwowany w czasie działania, nie deklarowany przy budowaniu
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Control Center pisze na każdym ekranie, skąd są liczby. Dotąd brało to zdanie z
 * `config.dataOrigin`, czyli ze zmiennej ustawionej przy BUDOWANIU paczki. To jest twierdzenie
 * o tym, na co aplikację zbudowano — nie o tym, z czym rozmawia w tej chwili.
 *
 * Ta różnica przestała być teoretyczna z chwilą, gdy backend nauczył się startować w trybie
 * `PROD_READONLY`: TA SAMA paczka `/cc/` może być podana obok backendu testowego i obok
 * backendu czytającego produkcję. Napis „dane kontrolowane (syntetyczne)" nad prawdziwym
 * wolumenem firmy jest wtedy po prostu nieprawdziwy — a właściciel czytający „syntetyczne"
 * nad realną sprzedażą podejmie decyzję na fałszywym założeniu.
 *
 * Błąd odwrotny jest groźniejszy: napis „produkcja" nad zbiorem testowym każe potraktować
 * liczby testowe jak wyniki firmy.
 *
 * Dlatego zdanie o źródle pochodzi teraz z ODPOWIEDZI backendu (`source_mode`), a konfiguracja
 * budowania jest wyłącznie stanem zapasowym — używanym, dopóki nie przyszła ani jedna odpowiedź,
 * i opisanym wprost jako niepotwierdzony.
 */

import { config, describeDataOrigin } from '@/config/env';

/** Tryby, które backend potrafi zaraportować. Wartość nieznana NIE jest tłumaczona na domysł. */
export type TrybZrodla = 'TEST_CONTROLLED' | 'PROD_READONLY';

const TRYBY_ZNANE: readonly string[] = ['TEST_CONTROLLED', 'PROD_READONLY'];

let zaobserwowany: TrybZrodla | null = null;
let nieznanaWartosc: string | null = null;
const sluchacze = new Set<() => void>();

function powiadom(): void {
  for (const s of sluchacze) s();
}

/**
 * Zapisuje tryb z odpowiedzi API. Wołane przez klienta dla KAŻDEJ odpowiedzi — dzięki temu
 * pojedynczy endpoint nie może o tym zapomnieć.
 *
 * Wartość nieznana NIE jest po cichu ignorowana ani mapowana na tryb testowy: zostaje
 * zapamiętana jako nieznana i pokazana. Backend, który raportuje coś, czego front nie rozumie,
 * jest sytuacją do zauważenia, a nie do wygładzenia.
 */
export function zapiszTrybZOdpowiedzi(tresc: unknown): void {
  if (!tresc || typeof tresc !== 'object' || Array.isArray(tresc)) return;
  const wartosc = (tresc as Record<string, unknown>).source_mode;
  if (typeof wartosc !== 'string' || wartosc.length === 0) return;

  if (!TRYBY_ZNANE.includes(wartosc)) {
    if (nieznanaWartosc !== wartosc) { nieznanaWartosc = wartosc; powiadom(); }
    return;
  }
  const tryb = wartosc as TrybZrodla;
  if (zaobserwowany !== tryb || nieznanaWartosc !== null) {
    zaobserwowany = tryb;
    nieznanaWartosc = null;
    powiadom();
  }
}

/** Tryb zaobserwowany w odpowiedziach albo `null`, gdy żadna jeszcze nie przyszła. */
export function trybZaobserwowany(): TrybZrodla | null {
  return zaobserwowany;
}

export function nieznanyTryb(): string | null {
  return nieznanaWartosc;
}

/** Reset — wyłącznie dla testów. Stan modułowy między testami musi dać się wyczyścić. */
export function zresetujTrybZrodla(): void {
  zaobserwowany = null;
  nieznanaWartosc = null;
  powiadom();
}

export function subskrybujTrybZrodla(sluchacz: () => void): () => void {
  sluchacze.add(sluchacz);
  return () => { sluchacze.delete(sluchacz); };
}

/**
 * Zdanie o pochodzeniu danych — DOKŁADNIE to, które widzi człowiek.
 *
 * Kolejność jest istotna: najpierw to, co backend powiedział, potem dopiero konfiguracja
 * budowania — i ta druga zawsze z zaznaczeniem, że nie została potwierdzona.
 */
export function opiszZrodlo(): string {
  if (nieznanaWartosc) {
    return `Backend raportuje nieznany tryb źródła („${nieznanaWartosc}") — pochodzenie danych NIEUSTALONE`;
  }
  if (zaobserwowany === 'PROD_READONLY') {
    return 'Dane produkcyjne, tylko odczyt (potwierdzone przez backend)';
  }
  if (zaobserwowany === 'TEST_CONTROLLED') {
    return 'Dane kontrolowane (realne API + realny schemat, dane syntetyczne)';
  }
  // Żadnej odpowiedzi jeszcze nie było. Mówimy, co ZAKŁADAMY, i że to tylko założenie —
  // milczące podanie etykiety z konfiguracji budowania jako faktu byłoby tym samym błędem,
  // który ten moduł naprawia.
  return `${describeDataOrigin(config.dataOrigin)} — wg konfiguracji, jeszcze niepotwierdzone przez backend`;
}

/** Czy dane są produkcyjne. `null` znaczy „jeszcze nie wiadomo", i to NIE to samo co „nie". */
export function czyProdukcja(): boolean | null {
  if (nieznanaWartosc) return null;
  if (zaobserwowany === null) return config.dataOrigin === 'production' ? true : null;
  return zaobserwowany === 'PROD_READONLY';
}
